import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { COMMAND_PRIORITY_LOW, DROP_COMMAND, PASTE_COMMAND } from 'lexical';
import { useEffect, useRef } from 'react';
import { $insertImageNode } from '../ImagesPlugin';

const ACCEPTABLE_IMAGE_TYPES = ['image/', 'image/heic', 'image/heif', 'image/gif', 'image/webp'];

export default function DragDropPastePlugin({ onUploadImage }) {
  const [editor] = useLexicalComposerContext();
  const onUploadImageRef = useRef(onUploadImage);
  onUploadImageRef.current = onUploadImage;

  const handleImageFile = (file, domEvent) => {
    if (onUploadImageRef.current) {
      onUploadImageRef.current(file)
        .then((url) => {
          if (url) {
            editor.update(() => {
              $insertImageNode(editor, url);
            });
          }
        })
        .catch(() => {
          // Parent uploader surfaces the error; avoid unhandled promise noise here.
        });
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        const src = reader.result;
        editor.update(() => {
          $insertImageNode(editor, src);
        });
      };
      reader.readAsDataURL(file);
    }
    domEvent.preventDefault();
    return true;
  };

  const getImageFileFromItems = (items) => {
    if (!items) {
      return null;
    }
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (ACCEPTABLE_IMAGE_TYPES.some((type) => item.type.startsWith(type))) {
        const file = item.getAsFile();
        if (file) {
          return file;
        }
      }
    }
    return null;
  };

  useEffect(() => {
    const unregisterPaste = editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        const items = event instanceof ClipboardEvent ? event.clipboardData?.items : undefined;
        const file = getImageFileFromItems(items);
        if (file) {
          return handleImageFile(file, event);
        }
        return false;
      },
      COMMAND_PRIORITY_LOW
    );

    const unregisterDrop = editor.registerCommand(
      DROP_COMMAND,
      (event) => {
        const items = event instanceof DragEvent ? event.dataTransfer?.items : undefined;
        const file = getImageFileFromItems(items);
        if (file) {
          return handleImageFile(file, event);
        }
        return false;
      },
      COMMAND_PRIORITY_LOW
    );

    return () => {
      unregisterPaste();
      unregisterDrop();
    };
  }, [editor]);

  return null;
}
